import { generateImageThumbnail, getImageUploadS3SignedUrl, getProductThumbnailsS3SignedUrl } from '@/api/image';
import type {
    ImageFormats,
    ResizedFile,
    ResizedImageResponse,
    SignedUrlImageType,
    SignedUrlResponse
} from '@/api/typings/image';
import { buildCloudFrontUrl, getExtension } from './pathUtils';
import { uploadFileToS3 } from './s3Utils';
import { CanvasEx } from '@/typings/canvas';
import { clearBorder } from './elementsUtils';
import { BaseCanvasElement, CanvasElement, PathITextElement } from '@/typings/customilyElements/canvasElements';
import { Pattern } from 'fabric/fabric-impl';
import type { IUtil } from 'fabric/fabric-impl';
import { createCanvas, canvasToBlob, blobToBase64 } from './canvasUtils';
import {
    IMAGE_UPLOAD_ACCEPT,
    detectFileType,
    isValidFileSignature
} from '@/components/shared/CFileInput/utils/fileSignatureValidation';

/**
 * Thrown when a user-selected file's real signature (magic bytes) does not match
 * an image format, e.g. a PSD renamed to .jpg. Technical on purpose so it is
 * actionable in logs and error surfaces.
 */
export class InvalidImageSignatureError extends Error {
    constructor(
        public readonly fileName: string,
        public readonly detectedType: string | null,
        public readonly declaredExtension: string
    ) {
        super(
            `File '${fileName}' rejected: real content is '${
                detectedType ?? 'unrecognized'
            }' but the extension declares '.${declaredExtension}'. Only ${IMAGE_UPLOAD_ACCEPT} are allowed.`
        );
        this.name = 'InvalidImageSignatureError';
    }
}

// Signed-url types that upload raster/vector *image* files chosen by the user.
// Other types ('eps', 'mask') carry non-image payloads, and 'thumbnail' is
// generated internally, so they are not signature-checked here.
const SIGNATURE_CHECKED_IMAGE_TYPES: ReadonlySet<SignedUrlImageType> = new Set([
    'preview',
    'preview-mask',
    'library-categories',
    'product-bases',
    'libraries'
]);

const isFullUrl = (thumbUrl: string) => {
    try {
        new URL(thumbUrl);
        return true;
    } catch (e) {
        return false;
    }
};

export const fetchImageAsBlob = async (url: string): Promise<Blob> => {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch blob from: ' + url);
    return response.blob();
};

export const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            resolve({
                width: img.naturalWidth,
                height: img.naturalHeight
            });
        };

        img.onerror = reject;
        img.src = url;
    });
};

export const getThumbUrl = (thumb: string) => {
    return isFullUrl(thumb) ? thumb : buildCloudFrontUrl(thumb, true);
};

/**
 * Try to infer image format, defaults to PNG
 * @param image image to get format from
 */
export const inferFormatFromImage = (image: string | HTMLImageElement | HTMLCanvasElement | File): string => {
    // Default format
    if (!image) {
        return 'png';
    }

    // Try to get from filename
    if ((image as File).name) {
        const format = getExtension((image as File).name);
        if (format) return format;
    }

    // If this is a string, try to extract from base64 image
    if (typeof image === 'string' && image.includes('data:image/')) {
        const format = image.substring('data:image/'.length, image.indexOf(';base64'));
        return format;
    }

    // Default format
    return 'png';
};

export const imageToCanvas = async (
    image: HTMLImageElement | HTMLCanvasElement | string
): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
        const imageElementToCanvas = (image: HTMLImageElement | HTMLCanvasElement) => {
            if (image instanceof HTMLImageElement) {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.height = image.height;
                canvas.width = image.width;

                ctx?.drawImage(image, 0, 0);
                return canvas;
            }
            return image;
        };

        if (typeof image === 'string') {
            const img = new Image();
            img.onload = () => {
                resolve(imageElementToCanvas(img));
            };
            img.onerror = (_errorMsg, _url, _lineNumber, _column, error) => {
                reject(error);
            };
            img.crossOrigin = 'Anonymous';
            img.src = image;
        } else {
            resolve(imageElementToCanvas(image));
        }
    });
};

export const imageToBlob = (image: HTMLImageElement | HTMLCanvasElement | string | File): Promise<Blob | null> => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
        try {
            if (typeof image === 'string' && image.startsWith('data:')) {
                const response = await fetch(image);
                resolve(await response.blob());
                return;
            }

            // If the image is not a file, convert it
            if (!(image instanceof Blob) && !(image instanceof File)) {
                const canvas = await imageToCanvas(image);
                canvas.toBlob(function (blob) {
                    resolve(blob);
                });
            } else {
                resolve(image);
            }
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * Upload the given image to S3
 * @param type type of image to upload
 * @param image image to upload to S3
 * @param progressCallback callback to report file upload progress
 */
export const uploadImageToS3 = async (
    type: SignedUrlImageType,
    image: string | HTMLImageElement | HTMLCanvasElement | File,
    progressCallback?: (progress: number) => void
): Promise<SignedUrlResponse> => {
    // Defense-in-depth: only user-selected files (not internally generated
    // canvases/base64) can carry a mismatched signature. Guard the image types.
    if (image instanceof File && SIGNATURE_CHECKED_IMAGE_TYPES.has(type)) {
        if (!(await isValidFileSignature(image, IMAGE_UPLOAD_ACCEPT))) {
            throw new InvalidImageSignatureError(image.name, await detectFileType(image), inferFormatFromImage(image));
        }
    }

    const format = inferFormatFromImage(image);

    // Get signed URL
    let response;
    if (type !== 'thumbnail') {
        response = await getImageUploadS3SignedUrl(type, format as ImageFormats);
    } else {
        response = await getProductThumbnailsS3SignedUrl();
    }

    // Get Blob to upload
    const blob = await imageToBlob(image);

    if (blob) {
        // Upload to S3
        await uploadFileToS3(response.SignedUrl, response.Mime, blob, response.CacheControl, progressCallback);
        return response;
    }

    throw new Error('Could not convert image to blob');
};

export const uploadImageAndGenerateThumb = async (
    type: SignedUrlImageType,
    image: string | HTMLImageElement | HTMLCanvasElement | File,
    typedThumb = true,
    progressCallback?: (progress: number) => void
): Promise<SignedUrlResponse & { thumbnailPath: string }> => {
    const signedUrl = await uploadImageToS3(type, image, progressCallback);
    const thumbnail = await generateImageThumbnail(signedUrl.Name, typedThumb ? type : undefined);
    return { ...signedUrl, thumbnailPath: thumbnail.Path };
};

export const limitImageResolutionFromFile = async (file: File, maxWidth: number, maxHeight: number) => {
    return new Promise<ResizedFile>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const img = new Image();
                img.onload = async () => {
                    const { canvas, resized } = await limitImageResolution(img, maxWidth, maxHeight);
                    canvas.toBlob((blob) => {
                        const resizedFile = new File([blob!], file.name, { type: file.type });
                        resolve({
                            resized,
                            file: resizedFile
                        });
                    });
                };
                img.onerror = (__event, __url, __lineNumber, __column, error) => reject(error);
                img.crossOrigin = 'Anonymous';
                img.src = event.target?.result as string;
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};

/**
 * Limits the image resolution from a Javascript Image object maintaining
 *  the aspect ratio.
 * @param  {HTMLImageElement | HTMLCanvasElement | string}  img         Image object to resize
 * @param  {Number}                                         width       New image width
 * @param  {Number}                                         height      New image height
 * @return {Promise} Returns a promise with the new resized Image as result
 */
export const limitImageResolution = async (
    image: HTMLImageElement | HTMLCanvasElement,
    maxWidth: number,
    maxHeight: number
): Promise<ResizedImageResponse> => {
    const img = await imageToCanvas(image);

    let wasResized = false;
    let width = img.width;
    let height = img.height;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // Resize image if it's too big
    if (height > maxHeight || width > maxWidth) {
        wasResized = true;

        let ratio = 1;
        if (height > width) {
            ratio = height / maxHeight;
        } else if (width > height) {
            ratio = width / maxWidth;
        }
        // Width and height are the same
        else {
            ratio = width / maxWidth;
        }

        height /= ratio;
        width /= ratio;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.imageSmoothingQuality = 'high';
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(img, 0, 0, width, height);

    const base64 = canvas.toDataURL();
    return { canvas, base64, resized: wasResized };
};

const shouldReplaceBackground = (strColor: string | Pattern | undefined) => {
    if (typeof strColor !== 'string') return false;

    const cleanedColor = strColor.replace(/\s/g, '');

    switch (cleanedColor) {
        case 'white':
        case '#fff':
        case '#ffffff':
        case 'rgb(255,255,255)':
        case 'rgba(255,255,255,1)':
            return true;
        default:
            return false;
    }
};

export const getCurrentCanvasAsImage = async (
    templateName: string,
    canvas: CanvasEx,
    applyScale = false,
    downloadImage = true
) => {
    try {
        const previewCanvas = canvas;
        if (!previewCanvas) {
            console.error('Preview canvas not found');
            return;
        }

        // Set transparent background only for white background
        const bgReplacedForDownload = {
            wasReplaced: false,
            originalColor: previewCanvas.backgroundColor
        };
        if (shouldReplaceBackground(previewCanvas.backgroundColor)) {
            previewCanvas.backgroundColor = 'rgba(0,0,0,0)';
            bgReplacedForDownload.wasReplaced = true;
        }

        // Scale the canvas image
        let multiplier = 1;
        if (applyScale) {
            const targetSize = 2000;
            if (previewCanvas.getWidth() > previewCanvas.getHeight()) {
                multiplier = targetSize / previewCanvas.getWidth();
            } else {
                multiplier = targetSize / previewCanvas.getHeight();
            }
        }

        // Get the scaled image as a data URL
        const scaledDataUrl = previewCanvas.toDataURL({
            format: 'png',
            multiplier,
            quality: 1
        });

        if (!downloadImage) {
            if (bgReplacedForDownload.wasReplaced) {
                previewCanvas.backgroundColor = bgReplacedForDownload.originalColor;
            }
            return scaledDataUrl;
        }

        // Load the scaled image
        const img = new Image();
        img.onload = async () => {
            // Create a new canvas with a white background
            const finalCanvas = createCanvas(img.width, img.height);
            const ctx = finalCanvas.getContext('2d');
            if (!ctx) {
                console.error('Unable to get 2D context');
                return;
            }

            // Set transparent background
            ctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);

            // Draw the scaled image
            ctx.drawImage(img, 0, 0);

            // Convert to JPEG data URL
            const blob = await canvasToBlob(finalCanvas);
            const base64Image = await blobToBase64(blob);

            // Create a download link
            const link = document.createElement('a');
            link.href = base64Image;
            link.download = `${templateName.replace(/\s+/g, '_')}_preview.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Reset original canvas background color
            if (bgReplacedForDownload.wasReplaced) {
                previewCanvas.backgroundColor = bgReplacedForDownload.originalColor;
            }
        };
        img.src = scaledDataUrl;
        return scaledDataUrl;
    } catch (error) {
        console.error('Error downloading image:', error);
    }
};

export const handleDownloadCurrentCanvasAsImage = async (canvas: CanvasEx, productName: string) => {
    canvas.fire('selection:cleared');
    const canvasObjs = canvas.getObjects();
    for (const element of canvasObjs) {
        clearBorder(element as CanvasElement);
    }
    await getCurrentCanvasAsImage(productName, canvas);
    for (const canvasObject of canvasObjs) {
        if (!(canvasObject instanceof window.fabric.Object)) continue;
        const element = canvasObject as CanvasElement;
        if (element.type === 'path-itext') {
            element.set('pathWidth', canvas.getVisibleStrokeWidth());
        } else if (element.type === 'bounded-text') {
            element.setStrokeWidth(canvas.getVisibleStrokeWidth());
        } else if (element.type === 'text-transform') {
            element.setBoundingStrokeWidth(element.strokeWidthDefault);
        } else {
            (element as BaseCanvasElement).set('strokeWidth', canvas.getVisibleStrokeWidth());
        }
    }
};

type BoundingBox = {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
};

const getPathITextBounds = (obj: PathITextElement) => ({
    left: obj.left! - obj.width! / 2,
    top: obj.top! - obj.height! / 2,
    right: obj.left! + obj.width!,
    bottom: obj.top! + obj.height!,
    width: obj.width!,
    height: obj.height!
});

const getObjectBounds = (obj: fabric.Object) => {
    const { left, top, width, height } = obj.getBoundingRect(true);
    return { left, top, right: left + width, bottom: top + height, width, height };
};

const isValidBounds = (bounds: BoundingBox) => {
    return ![bounds.left, bounds.top, bounds.width, bounds.height].some(isNaN);
};

// Funciones auxiliares para mejorar la legibilidad y rendimiento
export const calculateBoundingBox = (elements: fabric.Object[]) => {
    const boundingBox: BoundingBox = {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
        width: 0,
        height: 0
    };

    for (const obj of elements) {
        const objBounds =
            obj.type === 'path-itext' ? getPathITextBounds(obj as PathITextElement) : getObjectBounds(obj);

        if (!isValidBounds(objBounds)) continue;
        boundingBox.left = Math.min(boundingBox.left, objBounds.left);
        boundingBox.top = Math.min(boundingBox.top, objBounds.top);
        boundingBox.right = Math.max(boundingBox.right, objBounds.left + objBounds.width);
        boundingBox.bottom = Math.max(boundingBox.bottom, objBounds.top + objBounds.height);
    }

    boundingBox.width = boundingBox.right - boundingBox.left;
    boundingBox.height = boundingBox.bottom - boundingBox.top;

    return boundingBox;
};

const isValidBoundingBox = (box: BoundingBox) => {
    return box.width > 0 && box.height > 0;
};

const createTempCanvas = (boundingBox: BoundingBox) => {
    const tempCanvas = document.createElement('canvas');
    const width = Math.ceil(boundingBox.width);
    const height = Math.ceil(boundingBox.height);

    // Optimizar el tamaño del canvas si es necesario
    const maxSize = 4096; // Tamaño máximo recomendado para la mayoría de navegadores
    const scale = Math.min(1, maxSize / Math.max(width, height));

    tempCanvas.width = Math.ceil(width * scale);
    tempCanvas.height = Math.ceil(height * scale);

    const tempCtx = tempCanvas.getContext('2d', {
        alpha: true,
        willReadFrequently: true
    });

    if (tempCtx && scale !== 1) {
        tempCtx.scale(scale, scale);
    }

    return { tempCanvas, tempCtx };
};

const renderElements = async (elements: fabric.Object[], ctx: CanvasRenderingContext2D, boundingBox: BoundingBox) => {
    // Desactivar la suavización si no es necesaria
    ctx.imageSmoothingEnabled = false;

    // Renderizar en chunks para evitar bloquear el hilo principal
    const CHUNK_SIZE = 10;
    for (let i = 0; i < elements.length; i += CHUNK_SIZE) {
        const chunk = elements.slice(i, i + CHUNK_SIZE);
        await new Promise((resolve) => {
            requestAnimationFrame(() => {
                chunk.forEach((obj) => {
                    ctx.save();
                    ctx.translate(-boundingBox.left, -boundingBox.top);
                    obj.render(ctx);
                    ctx.restore();
                });
                resolve(null);
            });
        });
    }
};

export const getCurrentSelectionAsImage = async (canvas: CanvasEx) => {
    try {
        const visibleElements = canvas
            .getObjects()
            .filter((obj) => obj.visible && !obj.isProductGuidelines && obj instanceof window.fabric.Object);

        if (visibleElements.length === 0) {
            throw new Error(DesignSnapshotErrors.NO_ELEMENTS);
        }

        // Calcular el bounding box en una sola pasada
        const boundingBox = calculateBoundingBox(visibleElements);

        if (!isValidBoundingBox(boundingBox)) {
            throw new Error(DesignSnapshotErrors.INVALID_BOUNDING_BOX);
        }

        // Crear y configurar el canvas temporal
        const { tempCanvas, tempCtx } = createTempCanvas(boundingBox);

        if (!tempCtx) {
            throw new Error(DesignSnapshotErrors.RENDERING_CONTEXT);
        }

        // Renderizar todos los elementos en batch
        await renderElements(visibleElements, tempCtx, boundingBox);

        const base64 = tempCanvas.toDataURL('image/png');
        return base64;
    } catch (error) {
        console.error(error);
        const base64 = await getCurrentCanvasAsImage('', canvas, false, false);
        return base64;
    }
};

export const loadAndResizeImageIntoBase64 = async (
    fabricUtil: IUtil,
    imageUrl: string,
    placeholderWidth: number,
    placeholderHeight: number
) => {
    return new Promise((resolve, reject) => {
        const imageUrlWithTimestamp = `${imageUrl}?_t=${new Date().getTime()}`;

        fabricUtil.loadImage(
            imageUrlWithTimestamp,
            async function (imgElement) {
                if (!imgElement) {
                    console.log('Failed to load image.');
                    return reject('Image load failed');
                }
                const hiddenCanvas = createCanvas(imgElement.naturalWidth, imgElement.naturalHeight);
                if (!hiddenCanvas) return;

                const hiddenContext = hiddenCanvas.getContext('2d');
                if (!hiddenContext) return reject('Image load failed');
                hiddenCanvas.width = imgElement.naturalWidth;
                hiddenCanvas.height = imgElement.naturalHeight;
                hiddenContext.drawImage(imgElement, 0, 0);
                const imageData = hiddenContext.getImageData(0, 0, hiddenCanvas.width, hiddenCanvas.height);
                const data = imageData.data;
                for (let i = 0; i < data.length; i += 4) {
                    if (data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255) {
                        data[i + 3] = 0;
                    }
                }
                hiddenContext.putImageData(imageData, 0, 0);
                const scaleX = placeholderWidth / imgElement.naturalWidth;
                const scaleY = placeholderHeight / imgElement.naturalHeight;
                const scaleFactor = Math.min(scaleX, scaleY);
                const targetWidth = Math.round(imgElement.naturalWidth * scaleFactor);
                const targetHeight = Math.round(imgElement.naturalHeight * scaleFactor);
                const outputCanvas = createCanvas(targetWidth, targetHeight);
                const outputContext = outputCanvas.getContext('2d');
                if (!outputContext) return reject('Failed to get output canvas context');
                outputCanvas.width = targetWidth;
                outputCanvas.height = targetHeight;
                outputContext.drawImage(
                    hiddenCanvas,
                    0,
                    0,
                    imgElement.naturalWidth,
                    imgElement.naturalHeight,
                    0,
                    0,
                    targetWidth,
                    targetHeight
                );
                const blob = await canvasToBlob(outputCanvas);
                const resizedBase64 = await blobToBase64(blob);
                resolve(resizedBase64);
            },
            null,
            'Anonymous'
        );
    });
};

export const getLoadedHtmlImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => {
            URL.revokeObjectURL(url);
            console.error('Failed to load image for stretch');
            reject();
        };

        image.src = url;
    });
};

const enum DesignSnapshotErrors {
    NO_ELEMENTS = 'No visible elements found.',
    INVALID_BOUNDING_BOX = 'Invalid bounding box dimensions.',
    RENDERING_CONTEXT = 'Failed to get rendering context for the snapshot canvas.'
}

export type ParseStrategy = 'url' | 'base64';
const parser: Record<
    ParseStrategy,
    {
        transform: (blob: Blob) => Promise<string> | string;
        cleanup?: (str: string) => void;
    }
> = {
    url: {
        transform: URL.createObjectURL,
        cleanup: URL.revokeObjectURL
    },
    base64: { transform: blobToBase64 }
};
export const loadImageFromBlob = async (blob: Blob, strategy: ParseStrategy = 'url'): Promise<HTMLImageElement> => {
    const converter = parser[strategy];
    const source = await converter.transform(blob);
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            converter.cleanup?.(source);
            resolve(img);
        };

        img.onerror = (err) => {
            converter.cleanup?.(source);
            reject(err);
        };

        img.src = source;
    });
};
